import angular from 'angular'
import createFocusTrap from 'focus-trap'

/**
 * Default focus trap options for initializing `focus-trap`
 */
const defaultFocusTrapOptions = {
  clickOutsideDeactivates: true,
  escapeDeactivates: false,
  active: true,
  paused: false,
  returnFocusOnDeactivate: false,
}

/**
 * Identifies a container where the focus should be trapped.

Directive options
```
{
  // If true, a click outside the focus trap will deactivate the focus trap
  clickOutsideDeactivates: boolean
  // If true, the Escape key will trigger deactivation of the focus trap
  escapeDeactivates: boolean
  // Determining whether the focus trap is activated or not
  active: boolean
  // By default, when a focus trap is activated the first element in the focus trap's tab order will receive focus. it can be a DOM node, or a selector string.
  initialFocus: string | DOMNode
}
```

Example:

```
<div vd-focus-trap="{escapeDeactivates: true}">
  <p>Here is a focus trap with some focusable parts.</p>
  <input type="text" value="input box..." />
  <a href="#">A Link</a>
  <input type="text" />
</div>
```
 */
angular.module('vend.ui').directive('vdFocusTrap', function ($timeout) {
  return {
    restrict: 'A',
    link(scope, $element, attrs) {
      const focusTrapOptions = angular.merge(
        {},
        defaultFocusTrapOptions,
        scope.$eval(attrs.vdFocusTrap) || {}
      )

      let focusTrap

      // By default an error will be thrown if the element contains no elements in its tab order. This ensures we
      // suppress the error while also ensuring the main container will receive the focus.
      function getFallbackFocusHandler(elementNode) {
        if (!elementNode.hasAttribute('tabindex')) {
          elementNode.setAttribute('tabindex', '-1')
        }

        return elementNode
      }

      $timeout(() => {
        const focusTrapElementDOMNode = $element[0]

        focusTrapOptions.fallbackFocus = getFallbackFocusHandler(
          focusTrapElementDOMNode
        )

        focusTrap = createFocusTrap(focusTrapElementDOMNode, focusTrapOptions)
        if (focusTrapOptions.active) {
          focusTrap.activate()
        }

        if (focusTrapOptions.paused) {
          focusTrap.pause()
        }
      })

      function attrObserver(value) {
        if (!value || !focusTrap) return

        const newOptions = scope.$eval(value)

        if (newOptions.active && !focusTrapOptions.active) {
          focusTrap.activate()
        } else if (!newOptions.active && focusTrapOptions.active) {
          focusTrap.deactivate()
        }

        if (newOptions.paused && !focusTrapOptions.paused) {
          focusTrap.pause()
        } else if (!newOptions.paused && focusTrapOptions.paused) {
          focusTrap.unpause()
        }

        angular.merge(focusTrapOptions, newOptions)
      }

      const stopObserving = attrs.$observe('vdFocusTrap', attrObserver, true)

      scope.$on('$destroy', () => {
        if (focusTrap) {
          focusTrap.deactivate()
        }
        stopObserving()
      })
    },
  }
})
